import dayjs from 'dayjs';
import { AtSign, ListChecks, LoaderCircle, Lock, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ReloadIcon } from '../lib/icons';
import { FlotoMark } from './room-messages';

/**
 * The research room, before there is one.
 *
 * A new session's page is still being read when the user arrives, and there
 * is no session, capture or room to open yet — the first capture is what
 * creates them. Rather than a progress panel that the room then replaces,
 * the user is in the room from the start: same frame, same header, and Floto
 * narrating the setup as the first notes of the thread. The room they are
 * handed over to keeps those notes (see `Arrival` in the room store), so the
 * handover is one more note arriving, not a new screen.
 */

/** Where a walk is, as the user would put it. */
export type WalkStage = 'boot' | 'load' | 'open' | 'save' | 'stitch' | 'walk';

/**
 * The walk's stages, then the session's own: its panel being written, or
 * seated and ready (or there being no panel to seat).
 */
export type SetupStage = WalkStage | 'nopanel' | 'panel' | 'ready';

export interface WalkProblem {
  title: string;
  body: string;
  /** A navigation that failed can be retried in the same browser. */
  onRetry?: () => void;
  /** Where to go instead, when retrying can't help. */
  exit?: { label: string; to: string };
}

/**
 * The boot's own steps. Time-driven, not event-driven, and deliberately so:
 * the worker protocol says nothing between "socket open" and `ready`
 * (Chromium is booting; there is no progress to report). The timings are
 * cosmetic — `ready` ends the stage whenever it actually arrives.
 */
const BOOT_STEPS = [
  { at: 0, label: 'Reserving a browser instance' },
  { at: 1400, label: 'Starting Chromium' },
  { at: 3400, label: 'Opening' },
] as const;

/** After this long it's a cold start, and saying so beats silence. */
const COLD_START_MS = 7000;

function useElapsed(running: boolean): number {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!running) return;
    const started = dayjs();
    const timer = setInterval(() => {
      setElapsed(dayjs().diff(started, 'millisecond'));
    }, 200);
    return () => {
      clearInterval(timer);
    };
  }, [running]);
  return elapsed;
}

/**
 * Floto setting the room up, as one card: what it is doing right now, and —
 * while the page is captured — how the room works, a step at a time, so the
 * wait teaches the room instead of just passing.
 *
 * The steps arrive with the setup: the first as the browser opens, the next
 * as the page is read, the last once it is on the board. Done ones get a
 * tick, the newest is lit, and three dots say Floto isn't finished.
 *
 * Live on `/browse`, and carried on (still) at the top of the room's own
 * thread, where the panel then joins and says hello.
 */
type Step = 'browser' | 'reading' | 'board' | 'panel';

const STEP_OF: Record<SetupStage, Step> = {
  boot: 'browser',
  load: 'reading',
  nopanel: 'panel',
  open: 'board',
  panel: 'panel',
  ready: 'panel',
  save: 'board',
  stitch: 'board',
  walk: 'reading',
};
const STEPS: Step[] = ['browser', 'reading', 'board', 'panel'];

const GUIDE = [
  {
    body: 'They join as soon as your page is ready. Open one to see their full profile.',
    icon: Users,
    title: 'Meet your personas',
  },
  {
    body: 'Everyone answers by default. Type @ in the message box to ask one persona directly.',
    icon: AtSign,
    title: 'Ask the room',
  },
  {
    body: 'As they talk, the fix list beside the room fills with the changes most worth making.',
    icon: ListChecks,
    title: 'Watch the fix list',
  },
] as const;

/**
 * How far along, 0–100. Never goes back: each stage starts past where the
 * one before can reach, and the two long waits — the boot and writing the
 * panel — creep on elapsed time rather than sitting still and reading as
 * hung.
 */
function percentOf(stage: SetupStage, screen: number, elapsed: number) {
  const creep = (from: number, to: number, ms: number) =>
    from + Math.min(elapsed / ms, 1) * (to - from);
  switch (stage) {
    case 'boot':
      return creep(2, 15, COLD_START_MS);
    case 'load':
      return 18;
    case 'walk':
      return 25 + Math.min(Math.max(screen, 1), 6) * 7;
    case 'stitch':
      return 72;
    case 'save':
      return 80;
    case 'open':
      return 86;
    case 'panel':
      return creep(88, 98, 30_000);
    default:
      return 100;
  }
}

export function ArrivalLog({
  host,
  phaseDetail = '',
  problem = null,
  screen,
  stage,
  still = false,
}: {
  host: string;
  /** What the load stage is waiting on, in a few words. */
  phaseDetail?: string;
  problem?: WalkProblem | null;
  /** Screens read so far, or in all once the walk is over. Unknown when the
   *  notes are rebuilt after a reload, which only stores the stitched page. */
  screen?: number;
  stage: SetupStage;
  /** Carried over from `/browse`, where the lines already arrived. */
  still?: boolean;
}) {
  const timed = (stage === 'boot' || stage === 'panel') && !problem;
  const elapsed = useElapsed(timed);
  const bootStep = BOOT_STEPS.reduce<string>(
    (found, step) => (elapsed >= step.at ? step.label : found),
    BOOT_STEPS[0].label,
  );
  const bootLabel = bootStep === 'Opening' ? `Opening ${host}` : bootStep;
  const screens =
    screen === undefined
      ? ''
      : `, ${String(screen)} ${screen === 1 ? 'screen' : 'screens'}`;

  const current = STEPS.indexOf(STEP_OF[stage]);
  const done = stage === 'ready';
  const said: Record<SetupStage, string> = {
    boot: `I'm opening ${host} in a real browser, the way a visitor would see it.`,
    load: `I'm capturing ${host}. It takes a few seconds.`,
    nopanel: "This room has no panel yet — add people to it and I'll start.",
    open: `Got it — ${host} is on the board${screens}.`,
    panel: `Now I'm finding people who'd actually use ${host}.`,
    ready: `Got it — ${host} is on the board${screens}.`,
    save: `Got it — ${host} is on the board${screens}.`,
    stitch: `I'm capturing ${host}. It takes a few seconds.`,
    walk: `I'm capturing ${host}. It takes a few seconds.`,
  };
  const doing: Partial<Record<SetupStage, string>> = {
    boot: bootLabel,
    load: phaseDetail || `Capturing ${host}…`,
    open: 'Putting it on the board…',
    panel: 'Writing your panel…',
    save: 'Saving it to the room…',
    stitch: 'Stitching the screens into one page…',
    walk: `Capturing ${host}…`,
  };
  const busy = problem ? undefined : doing[stage];
  const percent = Math.round(percentOf(stage, screen ?? 0, elapsed));
  // A step per setup step, the last one once the page is on the board.
  const shown = done ? GUIDE.length : Math.min(current + 1, GUIDE.length);
  const arrive = still
    ? ''
    : 'duration-300 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none';

  return (
    <section
      aria-label="Setting up the room"
      className={`overflow-hidden rounded-card border border-hairline bg-surface ${arrive}`}
    >
      <div className="border-b border-hairline px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-brand">
            <span className="brightness-0 invert">
              <FlotoMark />
            </span>
          </span>
          <p className="text-caption">
            <span className="font-semibold text-text-primary">Floto</span>
            <span className="text-text-muted"> · Assistant</span>
          </p>
        </div>
        <h3 className="!mt-2.5 !mb-0 !text-ui-strong !leading-snug !font-semibold !text-text-primary">
          Here&apos;s how the room works
        </h3>
        {problem ? (
          <div
            className="mt-1.5 border-l-2 border-l-critical pl-3 text-ui"
            role="alert"
          >
            <p className="font-semibold text-text-primary">{problem.title}</p>
            <p className="mt-1 text-text-secondary">{problem.body}</p>
            <ProblemActions problem={problem} />
          </div>
        ) : (
          <p className="mt-0.5 text-ui leading-snug text-text-secondary">
            {said[stage]}
          </p>
        )}
      </div>

      <div className="px-4 py-3.5">
        <ol className="space-y-3.5">
          {GUIDE.slice(0, shown).map((step, i) => (
            <GuideStep
              arrive={i === shown - 1 ? arrive : ''}
              done={done || i < shown - 1}
              index={i}
              key={step.title}
              step={step}
            />
          ))}
        </ol>

        {!done && !problem ? (
          <span aria-hidden="true" className="mt-3.5 ml-2.5 flex gap-1">
            {[0, 1, 2].map((dot) => (
              <span
                className="size-1.5 animate-pulse rounded-pill bg-room-violet motion-reduce:animate-none"
                key={dot}
                style={{ animationDelay: `${String(dot * 200)}ms` }}
              />
            ))}
          </span>
        ) : null}

        {busy ? (
          <div className="mt-4">
            <div className="flex items-center gap-1.5" role="status">
              <LoaderCircle
                aria-hidden="true"
                className="size-3.5 shrink-0 animate-spin text-room-violet motion-reduce:animate-none"
              />
              <span className="min-w-0 truncate text-caption font-medium text-room-violet">
                {busy}
              </span>
              <span className="ml-auto shrink-0 text-caption font-medium text-text-secondary tabular-nums">
                {percent}%
              </span>
            </div>
            <div
              aria-label="Setting up the room"
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={percent}
              className="mt-2 h-1.5 overflow-hidden rounded-pill bg-tag-minor"
              role="progressbar"
            >
              <div
                className="h-full rounded-pill bg-room-violet transition-[width] duration-700 ease-out"
                style={{ width: `${String(percent)}%` }}
              />
            </div>
            {stage === 'boot' && elapsed > COLD_START_MS ? (
              <p className="mt-2 text-caption text-text-muted duration-300 animate-in fade-in">
                No browser was warm, so one is booting from scratch. A few more
                seconds.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** One step of how the room works: its icon, then what to do. */
function GuideStep({
  arrive,
  done,
  index,
  step,
}: {
  arrive: string;
  done: boolean;
  index: number;
  step: (typeof GUIDE)[number];
}) {
  const Icon = step.icon;
  return (
    <li className={`flex items-start gap-3 ${arrive}`}>
      <span className="flex shrink-0 flex-col items-center">
        <span
          className={[
            'relative flex size-8 items-center justify-center rounded-button',
            done
              ? 'bg-violet-soft text-room-violet'
              : 'bg-room-violet text-white',
          ].join(' ')}
        >
          {/* No tick on a step behind: these are how the room works, not
              tasks — a tick read as "you did this". */}
          <Icon aria-hidden="true" className="size-4" />
        </span>
      </span>
      <div className="min-w-0">
        <p
          className={`text-badge font-semibold tracking-wider uppercase ${done ? 'text-text-secondary' : 'text-room-violet'}`}
        >
          Step {index + 1}
        </p>
        <p className="text-ui font-semibold text-text-primary">{step.title}</p>
        <p className="mt-0.5 text-caption leading-snug text-text-secondary">
          {step.body}
        </p>
      </div>
    </li>
  );
}

/** A setup that stopped: retry it, or go somewhere it can't fail. */
function ProblemActions({ problem }: { problem: WalkProblem }) {
  if (!problem.onRetry && !problem.exit) return null;
  return (
    <span className="mt-3 flex flex-wrap items-center gap-2">
      {problem.onRetry ? (
        <button
          className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-button bg-ink px-3.5 text-ui-strong font-semibold text-white transition hover:bg-ink/90 active:scale-[0.98]"
          onClick={problem.onRetry}
          type="button"
        >
          <ReloadIcon className="size-4 text-highlight" />
          Try again
        </button>
      ) : null}
      {problem.exit ? (
        <Link
          className="inline-flex h-9 items-center rounded-button border border-hairline-alt bg-surface px-3.5 text-ui-strong font-medium text-text-primary transition hover:bg-surface-hover active:scale-[0.98]"
          replace
          to={problem.exit.to}
        >
          {problem.exit.label}
        </Link>
      ) : null}
    </span>
  );
}

/**
 * The room's frame on `/browse`: the real room's header, with Floto's setup
 * notes as the thread and the composer waiting. No roster yet — the panel
 * joins in the room itself, each person saying hello as they arrive.
 */
export function ArrivalRoom({
  host,
  phaseDetail,
  problem,
  screen,
  stage,
}: {
  host: string;
  phaseDetail: string;
  problem: WalkProblem | null;
  screen: number;
  stage: WalkStage;
}) {
  // Follow the thread as notes arrive.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [stage, problem]);

  return (
    <aside className="relative flex h-full w-105 shrink-0 flex-col rounded-panel border border-hairline bg-warm-alt shadow-soft max-md:h-auto max-md:min-h-0 max-md:w-full max-md:flex-1 max-md:rounded-none max-md:border-0 max-md:shadow-none">
      <header className="shrink-0 rounded-t-panel border-b border-hairline bg-surface px-5 pt-4 pb-3">
        <div className="flex min-h-8 items-center gap-1.5">
          <h2 className="!m-0 min-w-0 flex-1 !text-heading-sm !leading-tight !font-semibold !text-text-primary">
            Feedback Room
          </h2>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4" ref={listRef}>
        <ArrivalLog
          host={host}
          phaseDetail={phaseDetail}
          problem={problem}
          screen={screen}
          stage={stage}
        />
      </div>

      <LockedComposer
        busy={!problem}
        label={problem ? 'The room is waiting on the page' : undefined}
      />
    </aside>
  );
}

/**
 * The room's composer while the room is being set up: shaped like the field
 * it will become, saying what is happening, with the send button busy. Not a
 * field yet, so nothing in it can be typed or pressed.
 */
export function LockedComposer({
  busy = true,
  label = 'Prepping your room…',
}: {
  /** Whether setup is moving. Off when it is stuck on something. */
  busy?: boolean;
  label?: string;
}) {
  return (
    <footer className="shrink-0 rounded-b-panel border-t border-hairline bg-surface px-4 pt-3 pb-4">
      <div
        aria-busy={busy}
        className="flex w-full items-center gap-2.5 rounded-pill bg-surface p-1.5 pl-5 ring-1 ring-hairline"
        role="status"
      >
        <span className="min-h-9 flex-1 py-1.5 text-ui leading-6 text-text-muted">
          {label}
        </span>
        <span className="flex size-9 shrink-0 items-center justify-center rounded-pill bg-brand text-white">
          {busy ? (
            <LoaderCircle
              aria-hidden="true"
              className="size-4 animate-spin motion-reduce:animate-none"
            />
          ) : (
            <Lock aria-hidden="true" className="size-4" />
          )}
        </span>
      </div>
    </footer>
  );
}
